/**
 * Segnalazioni — modale lato studente: pairing QR (spec §2 e §5).
 *
 * Flusso: crea pairing → QR (token) + short_code leggibile → attesa
 * identificazione via socket (`join_pairing` + evento `pairing_identified`)
 * CON polling REST parallelo ogni 4s (fallback kiosk con WS instabili) →
 * form segnalazione → submit. Scadenza (10 min) → stato errore con
 * "Genera nuovo codice".
 *
 * Socket: riuso del singleton condiviso `socketService` (admin-ui) basato su
 * createSocketService di frontend-shared.
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import QRCode from 'qrcode';
import { RefreshCw, X } from 'lucide-react';
import { socketService } from '../../services/socketService';
import { collectClientContext } from './collectClientContext';
import { createPairing, createStudentIssue, getPairingStatus } from './issuesApi';
import { IssueReportForm } from './IssueReportForm';
import type { ClientContext, IssueCreateResponse, IssuePairing } from './types';

/** Intervallo polling REST fallback (spec §2: ~4s). */
const POLL_INTERVAL_MS = 4000;

type PairingPhase = 'creating' | 'qr' | 'form' | 'submitted' | 'error';

export interface IssuePairingModalProps {
  /** Nome app per il contesto. */
  appName: string;
  onClose: () => void;
  /** Callback dopo creazione issue (es. toast). */
  onIssueCreated?: (issue: IssueCreateResponse) => void;
}

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 'var(--tl4k-z-modal)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0, 0, 0, 0.5)',
  padding: 'var(--tl4k-spacing-lg)',
};

const cardStyle: CSSProperties = {
  width: '100%',
  maxWidth: 480,
  maxHeight: 'min(90vh, 720px)',
  overflowY: 'auto',
  background: 'var(--tl4k-surface-default)',
  borderRadius: 'var(--tl4k-border-radius-xl)',
  boxShadow: 'var(--tl4k-shadow-xl)',
  padding: 'var(--tl4k-spacing-xl)',
  fontFamily: 'var(--tl4k-font-family-body)',
  position: 'relative',
};

/**
 * Modale pairing studente: QR + short code + attesa identificazione teacher,
 * poi form segnalazione.
 */
export function IssuePairingModal({ appName, onClose, onIssueCreated }: IssuePairingModalProps) {
  const [phase, setPhase] = useState<PairingPhase>('creating');
  const [pairing, setPairing] = useState<IssuePairing | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [context, setContext] = useState<ClientContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [teacherName, setTeacherName] = useState<string | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [created, setCreated] = useState<IssueCreateResponse | null>(null);

  const identifiedRef = useRef(false);
  const disposedRef = useRef(false);

  /** Passa al form dopo identificazione (idempotente: socket + polling). */
  const handleIdentified = useCallback((name?: string) => {
    if (identifiedRef.current || disposedRef.current) return;
    identifiedRef.current = true;
    if (name) setTeacherName(name);
    setPhase('form');
    void (async () => {
      try {
        const ctx = await collectClientContext({ appName });
        if (!disposedRef.current) setContext(ctx);
      } catch (e) {
        console.error('[IssuePairingModal] raccolta contesto fallita:', e);
        if (!disposedRef.current) setContext(null);
      }
    })();
  }, [appName]);

  /** Crea pairing, renderizza QR, aggancia socket + polling. */
  const startPairing = useCallback(async () => {
    identifiedRef.current = false;
    setError(null);
    setSubmitError(null);
    setPairing(null);
    setQrDataUrl(null);
    setPhase('creating');
    try {
      const createdPairing = await createPairing({
        app: appName,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      });
      if (disposedRef.current) return;
      setPairing(createdPairing);

      // QR del token pairing
      try {
        const dataUrl = await QRCode.toDataURL(createdPairing.token, {
          width: 280,
          margin: 1,
          errorCorrectionLevel: 'M',
        });
        if (!disposedRef.current) setQrDataUrl(dataUrl);
      } catch (e) {
        console.error('[IssuePairingModal] generazione QR fallita:', e);
        setError('Impossibile generare il QR: usa il codice manuale qui sotto.');
      }

      setPhase('qr');

      // Socket: join room pairing + attesa evento pairing_identified
      socketService.connect();
      socketService.emit('join_pairing', createdPairing.token);
    } catch (e) {
      console.error('[IssuePairingModal] creazione pairing fallita:', e);
      if (!disposedRef.current) {
        setError('Impossibile creare il codice di identificazione. Riprova.');
        setPhase('error');
      }
    }
  }, [appName]);

  // Socket listener (per l'intero ciclo di vita del modal)
  useEffect(() => {
    const onPairingIdentified = (data: { token?: string; teacher_name?: string }) => {
      if (!pairing || data?.token !== pairing.token) return;
      handleIdentified(data?.teacher_name);
    };
    socketService.on('pairing_identified', onPairingIdentified);
    return () => {
      socketService.off('pairing_identified', onPairingIdentified);
    };
  }, [pairing, handleIdentified]);

  // Avvio + ri-join del socket ad ogni nuovo pairing
  useEffect(() => {
    void startPairing();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al mount
  }, []);

  useEffect(() => {
    if (pairing) {
      socketService.emit('join_pairing', pairing.token);
    }
  }, [pairing]);

  // Polling REST parallelo (fallback) + countdown scadenza
  useEffect(() => {
    if (!pairing || phase === 'form' || phase === 'submitted') return;
    const interval = setInterval(() => {
      if (disposedRef.current || identifiedRef.current) return;

      const expiry = Date.parse(pairing.expires_at);
      if (Number.isFinite(expiry)) {
        const remaining = Math.max(0, Math.round((expiry - Date.now()) / 1000));
        setRemainingSeconds(remaining);
        if (remaining <= 0) {
          setError('Il codice è scaduto. Generane uno nuovo.');
          setPhase('error');
          return;
        }
      }

      void (async () => {
        try {
          const status = await getPairingStatus(pairing.token);
          if (disposedRef.current || identifiedRef.current) return;
          if (status.status === 'identified' || status.status === 'used') {
            handleIdentified(status.teacher_name);
          } else if (status.status === 'expired' || status.status === 'unknown') {
            setError('Il codice è scaduto o non è più valido. Generane uno nuovo.');
            setPhase('error');
          }
        } catch (e) {
          // Polling best-effort: log e riprova al giro successivo (socket primario).
          console.warn('[IssuePairingModal] polling stato pairing fallito:', e);
        }
      })();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [pairing, phase, handleIdentified]);

  // Cleanup on unmount
  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
    };
  }, []);

  const handleSubmit = async (description: string) => {
    if (!pairing) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const issue = await createStudentIssue({
        pairing_token: pairing.token,
        description: description || undefined,
        context: context ? (context as unknown as Record<string, unknown>) : undefined,
      });
      setCreated(issue);
      setPhase('submitted');
      onIssueCreated?.(issue);
    } catch (e) {
      console.error('[IssuePairingModal] invio segnalazione fallito:', e);
      setSubmitError(
        e instanceof Error && e.message
          ? `Invio non riuscito: ${e.message}`
          : 'Invio non riuscito. Riprova.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const minutesLeft =
    remainingSeconds !== null ? Math.max(0, Math.ceil(remainingSeconds / 60)) : null;

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="Segnala un problema">
      <div style={cardStyle}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Chiudi"
          style={{
            position: 'absolute',
            top: 'var(--tl4k-spacing-sm)',
            right: 'var(--tl4k-spacing-sm)',
            padding: 'var(--tl4k-spacing-xs)',
            border: 'none',
            background: 'transparent',
            color: 'var(--tl4k-neutral-600)',
            cursor: 'pointer',
            borderRadius: 'var(--tl4k-border-radius-sm)',
          }}
        >
          <X size={20} aria-hidden />
        </button>

        {(phase === 'creating' || phase === 'qr') && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--tl4k-spacing-md)',
              textAlign: 'center',
            }}
          >
            <h2 style={{ margin: 0, fontSize: 18, color: 'var(--tl4k-neutral-800)' }}>
              Segnala un problema
            </h2>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--tl4k-neutral-600)' }}>
              Mostra questo codice all'insegnante: dopo la scansione potrai descrivere il problema.
            </p>

            {phase === 'creating' && (
              <p style={{ color: 'var(--tl4k-neutral-600)', fontSize: 14 }}>
                Generazione del codice…
              </p>
            )}

            {phase === 'qr' && pairing && (
              <>
                {qrDataUrl ? (
                  <img
                    src={qrDataUrl}
                    alt={`QR per identificazione client, codice ${pairing.short_code}`}
                    width={280}
                    height={280}
                    style={{
                      borderRadius: 'var(--tl4k-border-radius-md)',
                      border: '1px solid var(--tl4k-neutral-300)',
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: 280,
                      height: 280,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px dashed var(--tl4k-neutral-400)',
                      borderRadius: 'var(--tl4k-border-radius-md)',
                      color: 'var(--tl4k-neutral-600)',
                      fontSize: 13,
                      padding: 'var(--tl4k-spacing-md)',
                    }}
                  >
                    QR non disponibile: comunica il codice qui sotto all'insegnante.
                  </div>
                )}

                {/* Short code leggibile (input manuale se la camera non va) */}
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: 12, color: 'var(--tl4k-neutral-600)' }}>
                    In alternativa, codice da inserire a mano:
                  </p>
                  <p
                    aria-label={`Codice manuale ${pairing.short_code}`}
                    style={{
                      margin: 0,
                      fontSize: 32,
                      fontWeight: 800,
                      letterSpacing: '0.35em',
                      color: 'var(--tl4k-primary-700)',
                      fontFamily: 'var(--tl4k-font-family-mono)',
                    }}
                  >
                    {pairing.short_code}
                  </p>
                </div>

                {minutesLeft !== null && (
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--tl4k-neutral-500)' }}>
                    In attesa dell'insegnante… (il codice scade tra ~{minutesLeft} min)
                  </p>
                )}

                {teacherName && (
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--tl4k-success)' }}>
                    Identificato da {teacherName}
                  </p>
                )}
              </>
            )}

            {error && (
              <p
                role="alert"
                style={{
                  margin: 0,
                  padding: 'var(--tl4k-spacing-sm)',
                  borderRadius: 'var(--tl4k-border-radius-md)',
                  background: 'var(--tl4k-error-surface)',
                  color: 'var(--tl4k-error)',
                  fontSize: 13,
                }}
              >
                {error}
              </p>
            )}
          </div>
        )}

        {phase === 'error' && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--tl4k-spacing-md)',
              textAlign: 'center',
            }}
          >
            <h2 style={{ margin: 0, fontSize: 18, color: 'var(--tl4k-neutral-800)' }}>
              {pairing ? 'Codice non più valido' : 'Errore di connessione'}
            </h2>
            <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--tl4k-error)' }}>
              {error ?? 'Il codice è scaduto o si è verificato un errore.'}
            </p>
            <button
              type="button"
              onClick={() => void startPairing()}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--tl4k-spacing-xs)',
                padding: 'var(--tl4k-spacing-sm) var(--tl4k-spacing-lg)',
                borderRadius: 'var(--tl4k-border-radius-md)',
                border: 'none',
                background: 'var(--tl4k-secondary-500)',
                color: 'var(--tl4k-surface-default)',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              <RefreshCw size={14} aria-hidden />
              Genera nuovo codice
            </button>
          </div>
        )}

        {phase === 'form' && pairing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--tl4k-spacing-md)' }}>
            {teacherName && (
              <p
                style={{
                  margin: 0,
                  fontSize: 13,
                  color: 'var(--tl4k-success)',
                  background: 'var(--tl4k-success-surface)',
                  padding: 'var(--tl4k-spacing-xs) var(--tl4k-spacing-sm)',
                  borderRadius: 'var(--tl4k-border-radius-sm)',
                }}
              >
                Client identificato{teacherName ? ` da ${teacherName}` : ''}. Ora puoi descrivere il problema.
              </p>
            )}
            {!context && (
              <p style={{ margin: 0, fontSize: 12, color: 'var(--tl4k-neutral-500)' }}>
                Raccolta dati di contesto in corso…
              </p>
            )}
            <IssueReportForm context={context} onSubmit={handleSubmit} submitting={submitting} error={submitError} />
          </div>
        )}

        {phase === 'submitted' && created && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--tl4k-spacing-md)',
              textAlign: 'center',
            }}
          >
            <h2 style={{ margin: 0, fontSize: 18, color: 'var(--tl4k-success)' }}>
              Segnalazione inviata
            </h2>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--tl4k-neutral-700)' }}>
              Grazie! La segnalazione <strong>{created.short_id}</strong> è stata registrata.
            </p>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: 'var(--tl4k-spacing-sm) var(--tl4k-spacing-lg)',
                borderRadius: 'var(--tl4k-border-radius-md)',
                border: '1px solid var(--tl4k-neutral-300)',
                background: 'var(--tl4k-surface-default)',
                color: 'var(--tl4k-neutral-700)',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              Chiudi
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default IssuePairingModal;
